'use client';

import Image from 'next/image';

import {
  usePawPrintCarousel,
  formatPawPrintDate,
  formatPawPrintName,
  type PawPrint
} from './usePawPrintCarousel';
import styles from './breedPawPrints.module.scss';

type BreedPawPrintsProps = {
  breedName: string;
  pawPrints: PawPrint[];
};

const PROFILE_PLACEHOLDER = '/icons/profile-placeholder.svg';

export function BreedPawPrints({ breedName, pawPrints }: BreedPawPrintsProps) {
  const {
    currentIndex,
    currentPawPrint,
    goToPrevious,
    goToNext,
    selectPawPrint,
    handleKeyDown
  } = usePawPrintCarousel({ pawPrints });

  if (pawPrints.length === 0) {
    return null;
  }

  return (
    <section
      className={styles.pawPrints}
      aria-label={`${breedName} Paw Prints Carousel`}
      onKeyDown={handleKeyDown}
      tabIndex={0}
    >
      <h2 className={styles.title}>Paw Prints 🐾</h2>
      <div className={styles.carousel}>
        <button
          className={`${styles.button} ${styles.buttonPrev}`}
          onClick={goToPrevious}
          aria-label="Previous Paw Print"
        >
          &#10094;
        </button>
        <div className={styles.item}>
          <header className={styles.header}>
            <Image
              className={styles.image}
              src={currentPawPrint?.image || PROFILE_PLACEHOLDER}
              alt={currentPawPrint?.alt || 'Profile placeholder'}
              width={64}
              height={64}
            />
            <div className={styles.info}>
              <div className={styles.name}>
                {formatPawPrintName(
                  currentPawPrint?.firstName ?? null,
                  currentPawPrint?.lastName ?? null
                )}
              </div>
              <div className={styles.parentTitle}>{`${breedName} Parent`}</div>
              <div className={styles.date}>
                {formatPawPrintDate(currentPawPrint?.createdAt ?? null)}
              </div>
            </div>
          </header>
          <div className={styles.content}>{currentPawPrint?.content}</div>
        </div>
        <button
          className={`${styles.button} ${styles.buttonNext}`}
          onClick={goToNext}
          aria-label="Next Paw Print"
        >
          &#10095;
        </button>
      </div>
      <div className={styles.dots}>
        {pawPrints.map((_, index) => (
          <button
            key={index}
            className={`${styles.dot} ${currentIndex === index ? styles.dotActive : ''}`}
            onClick={() => selectPawPrint(index)}
            aria-label={`Select paw print ${index + 1}`}
            aria-pressed={currentIndex === index}
          />
        ))}
      </div>
    </section>
  );
}
